<template>
  <div>
    <!-- Botón flotante -->
    <button class="chatbot-ia-button" @click="toggleChat" aria-label="Abrir chat IA">
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
      </svg>
    </button>

    <!-- Ventana de chat -->
    <transition name="fade">
      <div v-if="open" :class="['chatbot-ia-window', { maximized: isMaximized }]" role="dialog" aria-label="Chat IA">
        <div class="chatbot-ia-header">
          <div class="header-left">
            <button class="icon-btn" @click="toggleMax" :title="isMaximized ? t('restore') : t('maximize')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <template v-if="!isMaximized">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </template>
                <template v-else>
                  <polyline points="4 14 10 14 10 20"/>
                  <polyline points="20 10 14 10 14 4"/>
                  <line x1="14" y1="10" x2="21" y2="3"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </template>
              </svg>
            </button>
            <span>{{ t('title') }}</span>
          </div>

          <div class="header-actions">
            <div class="segmented">
              <button :class="['seg-btn', { active: language === 'es' }]" @click="setLang('es')">ES</button>
              <button :class="['seg-btn', { active: language === 'en' }]" @click="setLang('en')">EN</button>
            </div>
            <button class="icon-btn warn" @click="clearChat" :title="t('clear')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
            </button>
            <button class="icon-btn" @click="toggleChat" :title="t('close')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <!-- Mensajes -->
        <div class="chatbot-ia-messages" ref="messagesBox">
          <div v-for="(msg, i) in messages" :key="i" :class="['message', msg.role]">
            <div v-if="msg.role === 'bot'" class="bubble bot">
              <span class="bubble-text">{{ msg.text }}</span>
            </div>
            <div v-else-if="msg.role === 'user'" class="bubble user">
              <span class="bubble-text">{{ msg.text }}</span>
            </div>
            <div v-else-if="msg.role === 'typing'" class="bubble bot typing">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- Entrada -->
        <div class="chatbot-ia-input">
          <input
            v-model="question"
            :placeholder="t('placeholder')"
            @keyup.enter="ask"
            :disabled="isLoading"
            aria-label="Entrada de texto"
          />
          <button @click="ask" class="send-btn" :disabled="isLoading" aria-label="Enviar">➤</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import flowisePlugin from '~/plugins/flowise'

export default {
  name: 'ChatbotManuals',

  data() {
    const savedLang = localStorage.getItem('epc_chatia_lang') || 'es'
    const savedChatId =
      localStorage.getItem('epc_chatia_id') ||
      `chatia_${Date.now()}_${Math.random().toString(36).slice(2)}`

    localStorage.setItem('epc_chatia_id', savedChatId)

    return {
      open: false,
      isMaximized: false,
      isLoading: false,
      question: '',
      messages: [],
      language: savedLang,
      chatId: savedChatId,
      CHATFLOW_ID: '1e01a8c5-f1a7-4947-9c96-ea8135701589',
    }
  },

  methods: {
    async ask() {
      const q = this.question.trim()
      if (!q || this.isLoading) return

      this.question = ''
      this.isLoading = true
      this.messages.push({ role: 'user', text: q })
      this.messages = flowisePlugin.addTypingMessage(this.messages)
      this.scrollToBottom()

      try {
        const payload = {
          chatflowId: this.CHATFLOW_ID,
          question: q,
          chatId: this.chatId,
        }

        const response = await flowisePlugin.sendPrediction(payload, this.$store.getters['auth/token'])

        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.messages.push({ role: 'bot', text: response.data.text || JSON.stringify(response.data) })
      } catch (e) {
        console.error(e)
        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.messages.push({ role: 'bot', text: this.t('error') })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    t(key) {
      return flowisePlugin.translate(this.language, key, this.getDictionaries())
    },

    getDictionaries() {
      return {
        es: {
          title: 'Asistente IA',
          maximize: 'Maximizar',
          restore: 'Restaurar',
          clear: 'Limpiar chat',
          close: 'Cerrar',
          confirmClear: '¿Seguro que quieres limpiar el chat?',
          placeholder: 'Escribe tu pregunta…',
          systemMessage: 'Eres un asistente inteligente de EPC Tracker. Responde siempre en español.',
          error: '⚠️ Error al conectar con el asistente',
          welcome: '👋 ¡Hola! Soy tu asistente IA sobre el uso de EPC Tracker. ¿En qué puedo ayudarte?',
          langChanged: 'Idioma cambiado a Español.',
        },
        en: {
          title: 'AI Assistant',
          maximize: 'Maximize',
          restore: 'Restore',
          clear: 'Clear chat',
          close: 'Close',
          confirmClear: 'Are you sure you want to clear the chat?',
          placeholder: 'Type your question…',
          systemMessage: 'You are an intelligent EPC Tracker assistant. Always answer in English.',
          error: '⚠️ Error connecting to the assistant',
          welcome: '👋 Hi! I am your AI assistant about the use of EPC Tracker. How can I help you?',
          langChanged: 'Language switched to English.',
        },
      }
    },

    setLang(code) {
      const result = flowisePlugin.setLanguage({
        currentLang: this.language,
        newLang: code,
        messages: this.messages,
        storageKey: 'epc_chatia_lang',
        dictionaries: this.getDictionaries(),
      })

      if (result) {
        this.language = result.language
        this.messages = result.messages
        this.$nextTick(this.scrollToBottom)
      }
    },

    toggleChat() {
      this.open = !this.open
      if (this.open && this.messages.length === 0) {
        this.messages.push({ role: 'bot', text: this.t('welcome') })
      }
      this.$nextTick(this.scrollToBottom)
    },

    toggleMax() {
      this.isMaximized = !this.isMaximized
      this.$nextTick(this.scrollToBottom)
    },

    clearChat() {
      const result = flowisePlugin.clearChat({
        messages: this.messages,
        attachedFiles: null,
        storageKey: 'epc_chatia_id',
        dictionaries: this.getDictionaries(),
        language: this.language,
      })

      if (result) {
        this.messages = []
        this.chatId = result.chatId
        this.messages.push({ role: 'bot', text: this.t('welcome') })
        this.$nextTick(this.scrollToBottom)
      }
    },

    scrollToBottom() {
      flowisePlugin.scrollToBottom(this.$refs.messagesBox)
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity .2s; }
.fade-enter,
.fade-leave-to { opacity: 0; }

/* Botón flotante: barra horizontal por defecto, infla a círculo al hover */
.chatbot-ia-button {
  position: fixed;
  right: 92px;
  bottom: 12px;
  width: 56px;
  height: 14px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  color: #fff;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3B81F6, #2563EB);
  box-shadow: 0 2px 10px rgba(59, 129, 246, .4);
  transition: width .25s ease, height .25s ease, border-radius .25s ease, transform .2s, box-shadow .2s;
  z-index: 2147483646;
}
.chatbot-ia-button svg {
  transition: transform .2s ease;
}
.chatbot-ia-button:hover {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(59, 129, 246, .55);
}
.chatbot-ia-button:hover svg {
  transform: scale(1);
}
/* Icono reducido en estado strip */
.chatbot-ia-button:not(:hover) svg {
  transform: scale(0.55);
}

/* Ventana (alineada con el botón) */
.chatbot-ia-window {
  position: fixed;
  right: 92px;
  bottom: 84px;
  width: 400px;
  height: min(540px, calc(100vh - 130px));
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(0, 0, 0, .28);
  z-index: 2147483646;
  box-sizing: border-box;
}
.chatbot-ia-window.maximized {
  top: 5vh;
  bottom: auto;
  right: auto;
  left: 50%;
  transform: translateX(-50%);
  width: min(900px, 92vw);
  height: 90vh;
  border-radius: 18px;
}

/* Cabecera */
.chatbot-ia-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #3B81F6, #2563EB);
  color: #fff;
  padding: 12px 16px;
  font-weight: 600;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Botones cabecera */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, .12);
  color: #fff;
  transition: background .15s, transform .05s;
  font-size: 18px;
  line-height: 1;
}
.icon-btn:hover { background: rgba(255, 255, 255, .22); }
.icon-btn:active { transform: scale(.97); }
.icon-btn.warn { background: rgba(255, 77, 79, .18); }
.icon-btn.warn:hover { background: rgba(255, 77, 79, .28); }

/* Selector idioma */
.segmented {
  display: inline-flex;
  background: rgba(255, 255, 255, .14);
  border-radius: 10px;
  padding: 2px;
}
.seg-btn {
  border: none;
  cursor: pointer;
  color: #fff;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1;
  transition: background .15s;
}
.seg-btn.active { background: rgba(255, 255, 255, .28); }
.seg-btn:hover { background: rgba(255, 255, 255, .22); }

/* Mensajes */
.chatbot-ia-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
  background: #f5f7fb;
}
.message { display: flex; margin: 8px 0; }
.message.bot { justify-content: flex-start; }
.message.user { justify-content: flex-end; }
.bubble {
  max-width: 85%;
  display: inline-flex;
  flex-direction: column;
}
.bubble-text {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .08);
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
}
.bubble.user .bubble-text {
  background: #3B81F6;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.bubble.bot .bubble-text {
  background: #fff;
  color: #333;
  border-bottom-left-radius: 4px;
}

/* Typing */
.typing {
  background: #fff;
  color: #666;
  border-radius: 14px;
  padding: 10px 12px;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .06);
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #93c5fd;
  display: inline-block;
  animation: blink 1s infinite ease-in-out;
}
.dot:nth-child(2) { animation-delay: .15s; }
.dot:nth-child(3) { animation-delay: .30s; }
@keyframes blink {
  0%, 80%, 100% { opacity: .2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
}

/* Input */
.chatbot-ia-input {
  flex: 0 0 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #dbeafe;
  background: #fff;
  padding: 10px;
}
.chatbot-ia-input input {
  flex: 1;
  height: 40px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  background: #fff;
}
.send-btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  font-size: 16px;
  color: #fff;
  background: #3B81F6;
}
.send-btn:disabled,
.chatbot-ia-input input:disabled {
  opacity: .6;
  cursor: not-allowed;
}

/* Móvil */
@media (max-width: 600px) {
  .chatbot-ia-button {
    right: 24px;
  }
  .chatbot-ia-window {
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }
  .chatbot-ia-window.maximized {
    left: 0;
    transform: none;
    width: 100%;
    height: 100vh;
    top: 0;
    border-radius: 0;
  }
}
</style>
